import {
  DestinationType,
  FilterOptionSliceType,
  GuestData,
} from '../../../../interfaces/src/lib/types/index';
import { mscGet, mscRemove } from '../utilFuncs/mscStorageClient';
import {
  IGuestFilterState,
  MyAreaPillsConfiguration,
  MyAreaTooltipsConfiguration,
} from '../../../../interfaces/src/lib/interfaces/index';
import { FilterTypes } from '../../../../interfaces/src/lib/enums/index';
import { setSearchButtonClicked } from '../stateManager/slices/searchButtonHandlingSlice';
import {
  resetFlyAndCruiseFilters,
  resetNumberOfNightsFilters,
  resetPromotionFilters,
  resetTagFilters,
} from '../stateManager/slices/genericFilterSlice';
import { AppDispatch } from '../stateManager/store';
import {
  resetAirportFilters,
  resetCabinFilters,
  resetCategoryFilters,
  resetCruiseIdFilters,
  resetDestinationExcursionsFilters,
  resetDestinationFilters,
  resetFinalPriceRangeFilters,
  resetPortExcursionsFilters,
  resetPortFilters,
  resetPortOfCallFilters,
  resetPriceCodeFilters,
  resetPriceRangeFilters,
  resetPriceTypeFilters,
  resetShipFilters,
} from '../stateManager/slices/facetFilterSlice';
import { resetGuestFilter } from '../stateManager/slices/guestFilterSlice';
import { resetDateFilter } from '../stateManager/slices/dateFilterSlice';
import { setPageIndexAndNumber } from '../stateManager/slices/paginationSlice';
import {
  getDealsFilters,
  getDealsFiltersEditable,
  getHitsPerPage,
} from '../utilFuncs/contextAwareUtils';
import { addDealsFilterUserCleared } from '../stateManager/slices/dealsConfigSlice';
import { setLastFilterUpdated } from '../stateManager/slices/lastFilterUpdate';
import {
  handleSearch,
  handleSearchButton,
  handleSearchMultifilter,
} from '../utilitiesService/HandleSearch';
import { checkFilterValueInFacetsData } from '../utilitiesService/buildSelectedFilterQuery';
import { isDealsPage } from '../utilFuncs/dealsManagement';
import { getQueryParam } from './analyticsUtils';
import { IFlightData } from '../stateManager/slices/departureflights';
import { BookingStepsType } from '../stateManager/slices/bookingStepsSlice';
import { ItineraryDetailsState } from '../stateManager/slices/itinerarySlice';
import { english } from 'flatpickr/dist/l10n/default.js'; // 'en' is default
import { French } from 'flatpickr/dist/l10n/fr.js';
import { German } from 'flatpickr/dist/l10n/de.js';
import { Austria } from 'flatpickr/dist/l10n/at.js';
import { Italian } from 'flatpickr/dist/l10n/it.js';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import { de, en, es, fr, it } from 'intl-tel-input/i18n';

export const breakpoints = {
  xs: 0,
  s: 360,
  md: 656,
  lg: 1024,
  xl: 1280,
  xxl: 1440,
};

export function scrollToTop(): void {
  document.body.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
}

export function toggleDisplayOverlay(force?: boolean): void {
  const body = document?.querySelector('body');
  if (body) {
    body.classList.toggle('-overlay', force);
  }
}

// get weekday names based on locale
export function getWeekdaysShort(startMonday = true) {
  const locale =
    document.querySelector('[name="localeCode"]')?.getAttribute('content')?.replace('_', '-') ||
    'en-US';
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const weekdays = [];
  for (let day = 0; day < 7; day++) {
    const date = new Date(Date.UTC(2021, 7, 1 + day));
    weekdays.push(formatter.format(date));
  }
  if (startMonday) {
    return weekdays.slice(1).concat(weekdays[0]);
  }
  return weekdays;
}

export function formatSelectedNumberOfNights(
  selectedNumberOfNights: FilterOptionSliceType[]
): string {
  const numberOfNights: string[] = [];

  for (const option of selectedNumberOfNights) {
    numberOfNights.push(
      ...option.value
        .split(',')
        .map((val) =>
          checkFilterValueInFacetsData('numberOfNights', null, val) ? `numberOfNights:"${val}"` : ''
        )
        .filter(Boolean)
    );
  }

  numberOfNights.sort(
    (a, b) =>
      Number.parseInt(a.match(/\d+/)?.[0] || '0') - Number.parseInt(b.match(/\d+/)?.[0] || '0')
  );
  return numberOfNights.length > 0 ? `(${numberOfNights.join(' OR ')})` : '';
}

export function formatCruiseId(selectedCruiseId: FilterOptionSliceType[]): string {
  const cruiseIds: string[] = selectedCruiseId
    .map((id) =>
      checkFilterValueInFacetsData('cruiseID', null, id.value) ? `cruiseID:"${id.value}"` : ''
    )
    .filter(Boolean);
  return cruiseIds.length > 0 ? `(${cruiseIds.join(' OR ')})` : '';
}

export function formatPriceCode(selectedPriceCode: FilterOptionSliceType[]): string {
  const priceCodes: string[] = selectedPriceCode
    .map((id) =>
      checkFilterValueInFacetsData('priceCode', null, id.value) ? `priceCode:"${id.value}"` : ''
    )
    .filter(Boolean);
  return priceCodes.length > 0 ? `(${priceCodes.join(' OR ')})` : '';
}

export function formatPriceType(selectedPriceType: FilterOptionSliceType[]): string {
  const priceTypes: string[] = selectedPriceType
    .map((id) =>
      checkFilterValueInFacetsData('priceType', null, id.value) ? `priceType:"${id.value}"` : ''
    )
    .filter(Boolean);
  return priceTypes.length > 0 ? `(${priceTypes.join(' OR ')})` : '';
}

export function formatPortOfCalls(selectedPortOfCall: FilterOptionSliceType[]): string {
  const portOfCalls: string[] = selectedPortOfCall
    .map((id) =>
      checkFilterValueInFacetsData('portOfCalls', null, id.value) ? `portOfCalls:"${id.value}"` : ''
    )
    .filter(Boolean);
  return portOfCalls.length > 0 ? `(${portOfCalls.join(' OR ')})` : '';
}

export function formatGuestsFilter(guestsState: IGuestFilterState): string {
  const newGuestState: Partial<IGuestFilterState> = {
    firstCabin: { ...guestsState.firstCabin },
    secondCabin: { ...guestsState.secondCabin },
  };
  const guestFilterInfo: string[] = [];
  const guestFilterInfoSecondCabin: string[] = [];

  if (newGuestState.firstCabin) {
    const firstCabin = newGuestState.firstCabin;
    checkFilterValueInFacetsData('noofAdults', null, firstCabin.adults.toString())
      ? guestFilterInfo.push(`(noofAdults=${firstCabin.adults})`)
      : guestFilterInfo.push(`(noofAdults=2)`);
    // Should be sent 0 if no minors or juniors are selected
    // 0 means that algolia will return only cabins that have no minors or juniors
    checkFilterValueInFacetsData('noofMinors', null, firstCabin.minors.toString())
      ? guestFilterInfo.push(`(noofMinors=${firstCabin.minors})`)
      : guestFilterInfo.push(`(noofMinors=0)`);
    checkFilterValueInFacetsData('noofJuniors', null, firstCabin.juniors.toString())
      ? guestFilterInfo.push(`(noofJuniors=${firstCabin.juniors})`)
      : guestFilterInfo.push(`(noofJuniors=0)`);
  }

  if (newGuestState?.secondCabin?.isActive) {
    const secondCabin = newGuestState.secondCabin;
    guestFilterInfoSecondCabin.push(`(noofAdults=${secondCabin.adults})`);
    guestFilterInfoSecondCabin.push(`(noofMinors=${secondCabin.minors})`);
    guestFilterInfoSecondCabin.push(`(noofJuniors=${secondCabin.juniors})`);
  }

  const finalFirstCabin = guestFilterInfo.join(' AND ');
  const finalSecondCabin = guestFilterInfoSecondCabin.join(' AND ');

  const isAccessible =
    newGuestState?.firstCabin?.isAccessible || newGuestState?.secondCabin?.isAccessible;
  const isCabinConnected = newGuestState?.secondCabin?.isConnectedCabin;

  const cabinsUrl = finalSecondCabin
    ? `${finalFirstCabin} OR ${finalSecondCabin}`
    : `${finalFirstCabin} `;
  const isAccessibleUrl =
    isAccessible && checkFilterValueInFacetsData('accessibleCabins', null, 'true')
      ? ` AND (accessibleCabins:"true")`
      : '';
  const isConnectedCabinUrl =
    isCabinConnected && checkFilterValueInFacetsData('connectedCabins', null, 'true')
      ? ` AND (connectedCabins:"true")`
      : '';
  return cabinsUrl + isAccessibleUrl + isConnectedCabinUrl;
}

//merge filter options that have FilterOptionSliceType as a type
export function mergeFilterOptionsSliceType(arr: FilterOptionSliceType[]): FilterOptionSliceType[] {
  return arr.reduce<FilterOptionSliceType[]>((acc, current) => {
    // Find if the current name already exists in the accumulator
    const existing = acc.find((item) => item.name === current.name);
    if (existing) {
      // Concatenate the value if the name exists
      existing.value += `,${current.value}`;
    } else {
      // Add the new entry if the name does not exist
      acc.push({ ...current });
    }
    return acc;
  }, []);
}

type MergedDestinationType = Omit<DestinationType, 'value'> & { value: string[] };

export function mergeItemsDestinations(array: DestinationType[]): DestinationType[] {
  const mergedItems: { [key: string]: MergedDestinationType } = {};

  array.forEach((item: DestinationType) => {
    if (!mergedItems[item.name]) {
      mergedItems[item.name] = { ...item, value: [item.value] };
    } else {
      mergedItems[item.name].value.push(item.value);
      mergedItems[item.name].selected = mergedItems[item.name].selected || item.selected;
      mergedItems[item.name].disabled = mergedItems[item.name].disabled && item.disabled;
    }
  });

  return Object.values(mergedItems).map((item) => ({
    ...item,
    value: item.value.join('-'),
  }));
}

//separate destinations items that have DestinationType based on value
export function seperateItemsDestinations(updatedFilterOptions: DestinationType[]) {
  const processedArray: DestinationType[] = [];

  updatedFilterOptions.forEach((item: DestinationType) => {
    const values = item.value.split('-');
    if (values.length > 1) {
      values.forEach((value) => {
        processedArray.push({ ...item, value: value });
      });
    } else {
      processedArray.push(item);
    }
  });
  return processedArray;
}

//separate destinations items that are stringified inside destinations filter array
export function seperateStringifiedItemsDestinations(elements: { keyValues: { value: string[] } }) {
  const processedArray: string[] = [];
  const elementKeyArray = elements?.keyValues?.value.map((item) => JSON.parse(item));
  elementKeyArray?.forEach((item) => {
    const values = item.destinationValue.split('-');
    if (values.length > 1) {
      values.forEach((value: string) => {
        processedArray.push(
          JSON.stringify({
            ...item,
            destinationValue: value,
          })
        );
      });
    } else {
      processedArray.push(JSON.stringify(item));
    }
  });
  return processedArray;
}

export const bindClickEventOnPseudoElement = (
  selector: string,
  allElementsSelected: boolean,
  pseudoElement: 'before' | 'after',
  callback: (event: MouseEvent) => void
) => {
  let elements = Array.from(document.querySelectorAll(selector));
  const isClosebyx = selector.split(' ').includes('.closedbyx');

  if (!allElementsSelected) {
    elements = elements.slice(0, 1);
  }

  elements.forEach((element) => {
    element.addEventListener('click', (event: Event) => {
      const target = event.currentTarget as HTMLElement;
      const pseudoStyle = getComputedStyle(target, `:${pseudoElement}`);

      if (pseudoStyle) {
        let top = parseFloat(pseudoStyle.getPropertyValue('top'));
        const height = parseFloat(pseudoStyle.getPropertyValue('height'));
        let left = parseFloat(pseudoStyle.getPropertyValue('left'));
        const width = parseFloat(pseudoStyle.getPropertyValue('width'));

        const { offsetX: x, offsetY: y } = event as MouseEvent;

        // Case 1: Handle absolute positions
        if (x > left && x < left + width && y > top && y < top + height) {
          callback(event as MouseEvent);
          return;
        } else {
          callback(event as MouseEvent);
        }

        // Case 2: Handle `.closedbyx` special condition

        // Handle the case where the pseudo element is not absolutely positioned
        // When the pseudo element is positioned within closebyx, it is located in the end of the parent element

        if (!top && !left && isClosebyx) {
          const rect = target.getBoundingClientRect();
          // pseudo element is located at the end of the parent element, so left is the width of the parent element minus the width of the pseudo element
          left = rect.width - width;

          // there is padding 32px to title element, where the pseudo element is located
          // the y axis is starting from the top of the parent element
          top = 32;

          // x should be between left of the pseudo element and the end-total width of the parent element
          //y should be between top of the pseudo element and the bottom of the pseudo element
          if (x > left && x < rect.width && y > top && y < top + height) {
            callback(event as MouseEvent);
            return;
          }
        }
      }
    });
  });
};

export const soundex = (name: string): string => {
  const soundexTable: { [key: string]: string } = {
    B: '1',
    F: '1',
    P: '1',
    V: '1',
    C: '2',
    G: '2',
    J: '2',
    K: '2',
    Q: '2',
    S: '2',
    X: '2',
    Z: '2',
    D: '3',
    T: '3',
    L: '4',
    M: '5',
    N: '5',
    R: '6',
  };

  // Retain the first letter and convert it to uppercase
  const firstLetter = name.charAt(0).toUpperCase();
  let encoded = firstLetter;

  let lastCode = soundexTable[firstLetter] || ''; // Code for the first letter

  for (let i = 1; i < name.length; i++) {
    const char = name.charAt(i).toUpperCase();
    if (soundexTable[char]) {
      const code = soundexTable[char];
      // Append the code if different from the last
      if (code !== lastCode) {
        encoded += code;
      }
      lastCode = code;
    } else {
      // Reset code for non-mapped characters
      lastCode = '';
    }
  }

  return encoded; // Do not truncate or pad
};

// Function to split text into words and generate a list of Soundex codes
export const soundexForText = (text: string): string[] => {
  // Split the text into words using spaces or punctuation
  const words = text.match(/\b\w+\b/g) || [];

  // Generate the Soundex for each word
  return words.map((word) => soundex(word));
};

// Function to compare two strings using Soundex
export const compareTextsBySoundex = (text1: string, text2: string): boolean => {
  const soundexList1 = soundexForText(text1);
  const soundexList2 = soundexForText(text2);

  // Check if all Soundex codes are the same in both lists (ignoring order)
  const sorted1 = soundexList1.sort().join(',');
  const sorted2 = soundexList2.sort().join(',');

  return sorted1 === sorted2;
};

export const triggerInfo = {
  source: 'displayed', // Default value
};

export const setTriggerSource = (source: string) => {
  triggerInfo.source = source;
};

/**
 * Main functionality: Scroll to the component
 * Secondary: Based on parameter checkIfVisible, it will check if the element is in the viewport before scrolling.
 * In mobile view also put inline:'start' so as header of accordion goes on top of page
 *
 * @param {HTMLElement} element - The element to scroll into view.
 * @param checkIfVisible - A boolean value indicating if the element should be checked if it is in the viewport before scrolling.
 */
export function scrollToComponent(element: HTMLElement, checkIfVisible = false) {
  const isMobile = window.innerWidth < breakpoints.lg;
  function scrollToWithOffset(element: Element, offset = -10) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });

    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  }

  const needToCheckIfVisible = checkIfVisible ? !isElementInViewport(element) : true;
  if (element && needToCheckIfVisible) {
    isMobile
      ? scrollToWithOffset(element)
      : element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Returns boolean value indicating if the element is in the viewport.
 *
 * @param {HTMLElement} element - The element to check if it is in the viewport.
 */
export function isElementInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

// Helper: skip reset only when classic deals page (filters locked)
const shouldSkipDealsFilterReset = (filterType: string) =>
  isDealsPage() &&
  !getDealsFiltersEditable() &&
  getDealsFilters().some(({ filterType: ft }) => ft === filterType);

const shouldSkipDealsDateReset = () =>
  isDealsPage() &&
  !getDealsFiltersEditable() &&
  (getDealsFilters().some(({ filterType }) => filterType === FilterTypes.START_DATE) ||
    getDealsFilters().some(({ filterType }) => filterType === FilterTypes.END_DATE));

const shouldSkipDealsGuestReset = (filterType: string) =>
  isDealsPage() &&
  !getDealsFiltersEditable() &&
  getDealsFilters().some(({ filterType: ft }) => ft === filterType);

// Function to clear all filters and their chips
export function handleClearAllFilters(dispatch: AppDispatch) {
  if (!shouldSkipDealsFilterReset(FilterTypes.NUMBER_OF_NIGHTS)) {
    dispatch(resetNumberOfNightsFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PROMOTION)) {
    dispatch(resetPromotionFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.TAG)) {
    dispatch(resetTagFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.FLY_AND_CRUISE)) {
    dispatch(resetFlyAndCruiseFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.DESTINATION)) {
    dispatch(resetDestinationFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.DESTINATION_EXCURSIONS)) {
    dispatch(resetDestinationExcursionsFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PORT_EXCURSIONS)) {
    dispatch(resetPortExcursionsFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PORT)) {
    dispatch(resetPortFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.CATEGORY)) {
    dispatch(resetCategoryFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.SHIP)) {
    dispatch(resetShipFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.CABIN)) {
    dispatch(resetCabinFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.AIRPORT)) {
    dispatch(resetAirportFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PRICE_RANGE)) {
    dispatch(resetPriceRangeFilters());
  }
  dispatch(resetFinalPriceRangeFilters());
  if (!shouldSkipDealsFilterReset(FilterTypes.CRUISE_ID)) {
    dispatch(resetCruiseIdFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PRICE_CODE)) {
    dispatch(resetPriceCodeFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PRICE_TYPE)) {
    dispatch(resetPriceTypeFilters());
  }
  if (!shouldSkipDealsFilterReset(FilterTypes.PORT_OF_CALL)) {
    dispatch(resetPortOfCallFilters());
  }

  if (!shouldSkipDealsGuestReset(FilterTypes.FIRST_CABIN_ACCESSIBILITY)) {
    dispatch(resetGuestFilter(FilterTypes.FIRST_CABIN_ACCESSIBILITY));
  } else if (!shouldSkipDealsGuestReset(FilterTypes.SECOND_CABIN_ACCESSIBILITY)) {
    dispatch(resetGuestFilter(FilterTypes.SECOND_CABIN_ACCESSIBILITY));
  }

  if (!shouldSkipDealsDateReset()) {
    dispatch(resetDateFilter());
  }
  // On static SEO pages, mark all deals filters as user-cleared so useDealsFilters won't re-apply
  if (getDealsFiltersEditable()) {
    getDealsFilters().forEach((f) => dispatch(addDealsFilterUserCleared(f.filterType)));
  }
  dispatch(setPageIndexAndNumber({ hitsNumberPerPage: getHitsPerPage(), activePageIndex: 0 }));
  dispatch(setLastFilterUpdated(''));
  dispatch(setSearchButtonClicked(true));
  handleSearch(dispatch);
  handleSearchButton(dispatch);
  handleSearchMultifilter(dispatch);
}

export function getElementSizes(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    bottom: rect.bottom,
    right: rect.right,
    width: rect.width,
    height: rect.height,
  };
}

export function removeZerosFromPrice(priceToRemoveZeros?: string) {
  const num = parseFloat(priceToRemoveZeros ?? '');
  if (priceToRemoveZeros?.includes('.') && !priceToRemoveZeros.endsWith('.00')) {
    return num.toFixed(2);
  } else {
    return num.toFixed(0);
  }
}

export function checkIfScrollable(tab: Element): void {
  const cmpTab = tab.querySelector<HTMLElement>('.cmp-tabs');
  if (!cmpTab || cmpTab.getAttribute('data-is-scrollable') !== 'true') return;
  let offset = 0;
  let lasItem: Element;
  let lastItemWidth: number | null | undefined;

  const tabList = cmpTab.querySelector<HTMLElement>('.cmp-tabs__tablist');
  if (!tabList) return;

  const btns = Array.from(cmpTab.querySelectorAll<HTMLElement>('.cmp-tab-scroll'));
  const [btnLeft, btnRight] = btns;
  const scrollBy = 300;

  const allItems = tabList.querySelectorAll('.cmp-tabs__tab');
  if (allItems && allItems.length > 0) {
    lasItem = allItems[allItems.length - 1];
    lastItemWidth = lasItem.getBoundingClientRect().width;
  }

  btnLeft?.addEventListener('click', () => {
    tabList.scrollTo({
      left: Math.max(0, tabList.scrollLeft - scrollBy),
      behavior: 'smooth',
    });
  });

  btnRight?.addEventListener('click', () => {
    const { scrollLeft, scrollWidth, clientWidth } = tabList;
    tabList.scrollTo({
      left: Math.min(scrollLeft + scrollBy, scrollWidth - clientWidth),
      behavior: 'smooth',
    });
    setOffset(tabList);
  });

  const isMobile = window.innerWidth <= breakpoints.md;
  // if is the port gallery tab lets keep only backdrop without the button in mobile
  const isPortGallery = cmpTab.closest('.cmp-portsgallery') ? true : false;

  const totalyHideButton = isMobile && !isPortGallery;

  const updateButtonVisibility = (): void => {
    if (!tabList) return;

    const EPS = 1;
    const { scrollLeft, scrollWidth, clientWidth } = tabList;

    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const atStart = scrollLeft <= EPS;
    const atEnd = maxScrollLeft - scrollLeft <= EPS;

    btnLeft?.classList.toggle('hide', atStart || totalyHideButton);
    btnRight?.classList.toggle('hide', atEnd || totalyHideButton);

    btnLeft?.classList.toggle('keep-after-left', !atStart && isPortGallery && isMobile);
    btnRight?.classList.toggle('keep-after-right', !atEnd && isPortGallery && isMobile);
  };

  const setOffset = (tabList: Element) => {
    if (tabList.scrollLeft > 0) offset = lastItemWidth ?? 0;
  };

  updateButtonVisibility();
  requestAnimationFrame(() => updateButtonVisibility());
  window.addEventListener('load', () => updateButtonVisibility());

  tabList.addEventListener('scroll', () => {
    setOffset(tabList);
    updateButtonVisibility();
  });

  // Drag-to-scroll (mouse)
  let isMouseDown = false;
  let startX = 0;
  let scrollStartX = 0;

  tabList.addEventListener('mousedown', (e: MouseEvent) => {
    isMouseDown = true;
    tabList.classList.add('dragging');
    startX = e.pageX - tabList.offsetLeft;
    scrollStartX = tabList.scrollLeft;
  });

  tabList.addEventListener('mouseleave', () => {
    isMouseDown = false;
    tabList.classList.remove('dragging');
  });

  tabList.addEventListener('mouseup', () => {
    isMouseDown = false;
    tabList.classList.remove('dragging');
  });

  tabList.addEventListener('mousemove', (e: MouseEvent) => {
    if (!isMouseDown) return;
    e.preventDefault();
    const x = e.pageX - tabList.offsetLeft;
    const walk = (x - startX) * 1.5; // scroll speed factor
    tabList.scrollLeft = scrollStartX - walk;
  });

  // Drag-to-scroll (touch)
  let touchStartX = 0;
  let touchScrollStart = 0;

  tabList.addEventListener('touchstart', (e: TouchEvent) => {
    touchStartX = e.touches[0].pageX;
    touchScrollStart = tabList.scrollLeft;
  });

  tabList.addEventListener('touchmove', (e: TouchEvent) => {
    const x = e.touches[0].pageX;
    const walk = (x - touchStartX) * 1.5;
    tabList.scrollLeft = touchScrollStart - walk;
  });

  // Scrolls tab item at the center of the tablist to reveal next and previous tabs
  const scrollTabItemAtCenter = () => {
    const tabItems = tabList.querySelectorAll<HTMLElement>('.cmp-tabs__tab');
    if (!tabItems || tabItems.length < 0) return;

    tabItems.forEach((tabItem) => {
      tabItem.addEventListener('click', () => {
        const containerWidth = tabList.offsetWidth;
        const tabCenter = tabItem.offsetLeft + tabItem.offsetWidth / 2;
        const scrollTo = tabCenter - containerWidth / 2;

        tabList.scrollTo({
          left: scrollTo,
          behavior: 'smooth',
        });
      });
    });
  };
  scrollTabItemAtCenter();
}

export const loadFromSessionStorage = (
  key: string,
  defaultValue:
    | IFlightData
    | GuestData[]
    | BookingStepsType
    | ItineraryDetailsState
    | NonNullable<unknown>
) => {
  mscRemove('session', 'isSuccessfulBooking');
  const status3D = getQueryParam('status_3d');
  const uppTransactionId = getQueryParam('uppTransactionId');
  const klarnaStatus = getQueryParam('status');
  const klarnaTrxId = getQueryParam('datatransTrxId');
  const isCard3dsReturn = !!(status3D && uppTransactionId);
  const isKlarnaReturn = !!(klarnaStatus && klarnaTrxId);
  if (isCard3dsReturn || isKlarnaReturn) {
    const storedData = mscGet<unknown>('session', key);
    if (storedData === null) return defaultValue;
    mscRemove('session', key);
    return storedData as any;
  } else {
    return defaultValue;
  }
};

export function getPageLocale() {
  const metaLocale =
    document.querySelector('meta[name="languageCode"]')?.getAttribute('content') || 'en';
  return metaLocale;
}

export const langLocaleMap: Record<string, unknown> = {
  en: english,
  fr: French,
  de: German,
  at: Austria,
  it: Italian,
  es: Spanish,
};

export const telInputLocaleSearch: any = {
  en: en,
  fr: fr,
  de: de,
  at: de,
  es: es,
  it: it,
};

/**
 * Checks the difficulty level and returns a formatted string.
 *
 * @param {string | undefined} difficulty - The difficulty level to check.
 * @returns {string} - The formatted difficulty level string.
 */
export function checkAndSetDifficulty(difficulty: string | undefined): string {
  const difficultyLevelToCheck = 'difficultyLevel';
  switch (difficulty?.toUpperCase()) {
    case 'EASY':
      return difficultyLevelToCheck + '_EASY';
    case 'MEDIUM':
      return difficultyLevelToCheck + '_MEDIUM';
    case 'DIFFICULT':
      return difficultyLevelToCheck + '_DIFFICULT';
    default:
      return '';
  }
}

export const defaultPillsVisibility: MyAreaPillsConfiguration = {
  bookedPillEnabled: true,
  promoPillEnabled: true,
  countdownPillEnabled: true,
};

export const defaultTooltipsVisibility: MyAreaTooltipsConfiguration = {
  promoTooltipEnabled: true,
};

/**
 * Converts a date string to ISO 8601 format (YYYY-MM-DD) based on the provided format.
 * Supports common date formats: YYYY/MM/DD, DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD-MM-YYYY, MM-DD-YYYY
 *
 * @param {string} dateValue - The date string to convert
 * @param {string | undefined} format - The format of the input date (e.g., "YYYY/MM/DD", "DD/MM/YYYY")
 * @returns {string | undefined} - The date in ISO 8601 format (YYYY-MM-DD) or undefined if conversion fails
 */
export function convertDateToISO(
  dateValue: string,
  format: string | undefined
): string | undefined {
  if (!dateValue || !format) {
    return undefined;
  }

  try {
    // Parse the date based on the format
    // Common formats: YYYY/MM/DD, DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD-MM-YYYY, MM-DD-YYYY
    const formatParts = format.split(/[\/\-]/);
    const dateParts = dateValue.split(/[\/\-]/);

    if (formatParts.length !== dateParts.length || dateParts.length !== 3) {
      return undefined;
    }

    let year = '';
    let month = '';
    let day = '';

    // Map format parts to date parts
    formatParts.forEach((part, index) => {
      if (part === 'YYYY') {
        year = dateParts[index];
      } else if (part === 'MM') {
        month = dateParts[index];
      } else if (part === 'DD') {
        day = dateParts[index];
      }
    });

    if (year && month && day) {
      // Create ISO 8601 format: YYYY-MM-DD
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  } catch (error) {
    console.error('Error converting date to ISO:', error);
  }

  return undefined;
}
